import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import DashboardLayout from "layout";
import ConfirmDialog from "components/ConfirmDialog";
import { useToast } from "components/Toast";
import { syncHotels, syncRegions } from "services/hotel";
import { getApiError } from "services/finance";

const HotelTools = () => {
  const { showToast } = useToast();
  const [confirmFull, setConfirmFull] = useState(false);

  // Both syncs run in the background, so success means "started".
  const hotels = useMutation({
    mutationFn: (full: boolean) => syncHotels(full),
    onSuccess: (res, full) => {
      showToast(res?.message || (full ? "Full hotel re-sync started" : "Hotel sync started"), "info");
      setConfirmFull(false);
    },
    onError: (error) => {
      showToast(getApiError(error, "Failed to start hotel sync"), "error");
      setConfirmFull(false);
    },
  });

  const regions = useMutation({
    mutationFn: syncRegions,
    onSuccess: (res) => showToast(res?.message || "Region sync started", "info"),
    onError: (error) => showToast(getApiError(error, "Failed to start region sync"), "error"),
  });

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={600} color="#101828">
            Hotel Tools
          </Typography>
          <Typography variant="body2" color="#475467">
            Sync hotel and region data from ETG
          </Typography>
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap="16px">
          <Box bgcolor="#FFFFFF" border="1px solid #E4E7EC" borderRadius="12px" p="20px">
            <Typography fontSize={16} fontWeight={600} color="#101828">
              Hotels
            </Typography>
            <Typography fontSize={13} color="#667085" mb="16px">
              Incremental sync picks up changes since the last run. A full re-sync reloads everything
              and takes much longer.
            </Typography>
            <Box display="flex" gap="8px" flexWrap="wrap">
              <Button
                variant="contained"
                disabled={hotels.isPending}
                onClick={() => hotels.mutate(false)}
                sx={{ textTransform: "none" }}
              >
                Sync hotels
              </Button>
              <Button
                variant="outlined"
                disabled={hotels.isPending}
                onClick={() => setConfirmFull(true)}
                sx={{ textTransform: "none" }}
              >
                Full re-sync
              </Button>
            </Box>
          </Box>

          <Box bgcolor="#FFFFFF" border="1px solid #E4E7EC" borderRadius="12px" p="20px">
            <Typography fontSize={16} fontWeight={600} color="#101828">
              Regions
            </Typography>
            <Typography fontSize={13} color="#667085" mb="16px">
              Refreshes the regions customers search by.
            </Typography>
            <Button
              variant="contained"
              disabled={regions.isPending}
              onClick={() => regions.mutate()}
              sx={{ textTransform: "none" }}
            >
              Sync regions
            </Button>
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmFull}
        title="Run a full hotel re-sync?"
        message="This reloads all hotel data from ETG in the background and can take a long time."
        confirmLabel="Start full re-sync"
        loading={hotels.isPending}
        onConfirm={() => hotels.mutate(true)}
        onClose={() => setConfirmFull(false)}
      />
    </DashboardLayout>
  );
};

export default HotelTools;
